export const quickTasksRouteTemplate = `/**
 * Vibeship DevTools - Tasks API Route
 * Endpoint: /api/vibeship/tasks
 */

import { NextRequest, NextResponse } from 'next/server';
import { readFileSync, statSync, existsSync } from 'fs';
import { join, relative } from 'path';
import { glob } from 'glob';

interface Task {
  id: string;
  type: 'checkbox' | 'todo' | 'fixme' | 'hack' | 'note' | 'bug' | 'warning';
  text: string;
  title: string;
  file: string;
  line: number;
  priority: 'low' | 'medium' | 'high';
  completed?: boolean;
  metadata?: Record<string, unknown>;
}

interface PhaseGroup {
  name: string;
  milestone: string;
  description: string;
  fileGroups: Array<{
    path: string;
    phase?: string;
    milestone?: string;
    tasks: Task[];
    progress: number;
  }>;
  totalTasks: number;
  completedTasks: number;
}

// Task patterns to search for
const TASK_PATTERNS = {
  // Markdown checkbox patterns
  checkbox: /^\\s*[-*+]\\s*\\[([ xX])\\]\\s+(.+)$/gm,
  // Code comment patterns
  todo: /(?:\\/\\/|#|\\/\\*|<!--)\\s*TODO:?\\s*(.+?)(?:\\*\\/|-->)?$/gi,
  fixme: /(?:\\/\\/|#|\\/\\*|<!--)\\s*FIXME:?\\s*(.+?)(?:\\*\\/|-->)?$/gi,
  hack: /(?:\\/\\/|#|\\/\\*|<!--)\\s*HACK:?\\s*(.+?)(?:\\*\\/|-->)?$/gi,
  note: /(?:\\/\\/|#|\\/\\*|<!--)\\s*NOTE:?\\s*(.+?)(?:\\*\\/|-->)?$/gi,
  bug: /(?:\\/\\/|#|\\/\\*|<!--)\\s*BUG:?\\s*(.+?)(?:\\*\\/|-->)?$/gi,
  warning: /(?:\\/\\/|#|\\/\\*|<!--)\\s*WARNING:?\\s*(.+?)(?:\\*\\/|-->)?$/gi,
};

export async function GET(request: NextRequest) {
  try {
    const url = new URL(request.url);
    const searchParams = url.searchParams;
    
    // Get settings from query params
    const settings = {
      scanDirectory: searchParams.get('scanDirectory') || searchParams.get('path') || 'tickets',
      maxDepth: parseInt(searchParams.get('maxDepth') || '5'),
      filePatterns: searchParams.get('filePatterns') || '**/*.md,**/*.mdx',
      excludePatterns: searchParams.get('excludePatterns') || '**/node_modules/**,**/.git/**',
      includeCodeComments: searchParams.get('includeCodeComments') === 'true'
    };

    const tasks = await scanForTasks(settings);
    const phaseGroups = transformToPhaseGroups(tasks, settings.scanDirectory);
    
    // Return in the format expected by the client
    return NextResponse.json({ tasks: phaseGroups });
  } catch (error) {
    console.error('Tasks API Error:', error);
    return NextResponse.json(
      { 
        error: 'Failed to scan tasks',
        message: error instanceof Error ? error.message : 'Unknown error'
      },
      { status: 500 }
    );
  }
}

async function scanForTasks(settings: any): Promise<Task[]> {
  const tasks: Task[] = [];
  const cwd = process.cwd();
  const scanPath = join(cwd, settings.scanDirectory);
  
  // Create directory if it doesn't exist
  if (!existsSync(scanPath)) {
    console.log(\`Directory \${scanPath} does not exist, returning empty tasks\`);
    return tasks;
  }
  
  try {
    const patterns = settings.filePatterns.split(',').map((p: string) => p.trim());
    
    for (const pattern of patterns) {
      const files = glob.sync(pattern, {
        cwd: scanPath,
        ignore: settings.excludePatterns.split(',').map((p: string) => p.trim()),
        absolute: false,
        nodir: true
      });
      
      for (const file of files) {
        const filePath = join(scanPath, file);
        const content = readFileSync(filePath, 'utf8');
        const relativePath = relative(cwd, filePath);
        
        // Always extract markdown tasks from .md files
        if (file.endsWith('.md') || file.endsWith('.mdx')) {
          extractMarkdownTasks(content, relativePath, tasks);
        }
        
        // Optionally extract code comment tasks
        if (settings.includeCodeComments) {
          extractCodeCommentTasks(content, relativePath, tasks);
        }
      }
    }
  } catch (error) {
    console.error(\`Error scanning \${scanPath}:\`, error);
  }
  
  return tasks;
}

function extractMarkdownTasks(content: string, filePath: string, tasks: Task[]): void {
  const lines = content.split('\\n');
  let taskIndex = 0;
  
  // Extract checkbox tasks
  const checkboxMatches = [...content.matchAll(TASK_PATTERNS.checkbox)];
  
  for (const match of checkboxMatches) {
    const lineNumber = content.substring(0, match.index).split('\\n').length;
    const isCompleted = match[1].toLowerCase() === 'x';
    const text = match[2].trim();
    
    tasks.push({
      id: \`\${filePath}-checkbox-\${taskIndex++}\`,
      type: 'checkbox',
      text: text,
      title: text,
      file: filePath,
      line: lineNumber,
      completed: isCompleted,
      priority: inferPriority(text),
    });
  }
}

function extractCodeCommentTasks(content: string, filePath: string, tasks: Task[]): void {
  const lines = content.split('\\n');
  
  lines.forEach((line, index) => {
    for (const [type, pattern] of Object.entries(TASK_PATTERNS)) {
      if (type === 'checkbox') continue; // Skip checkbox pattern here
      
      const matches = [...line.matchAll(pattern)];
      
      for (const match of matches) {
        tasks.push({
          id: \`\${filePath}-\${index}-\${type}-\${tasks.length}\`,
          type: type as Task['type'],
          text: match[1].trim(),
          title: match[1].trim(),
          file: filePath,
          line: index + 1,
          priority: inferPriority(match[1]),
        });
      }
    }
  });
}

function transformToPhaseGroups(tasks: Task[], scanDirectory: string): PhaseGroup[] {
  // Group tasks by directory structure
  const phaseMap = new Map<string, Map<string, Task[]>>();
  
  tasks.forEach((task) => {
    const relativePath = task.file.startsWith(scanDirectory) 
      ? relative(process.cwd(), task.file) 
      : task.file;
    const pathParts = relativePath.split('/');
    
    // Determine phase name from directory structure
    let phaseName = 'General';
    
    if (pathParts.length > 1) {
      // Use the first directory after scan directory as phase
      const firstDir = pathParts[0];
      // Capitalize first letter
      phaseName = firstDir.charAt(0).toUpperCase() + firstDir.slice(1);
    }
    
    // Initialize phase if needed
    if (!phaseMap.has(phaseName)) {
      phaseMap.set(phaseName, new Map());
    }
    
    const phase = phaseMap.get(phaseName)!;
    if (!phase.has(task.file)) {
      phase.set(task.file, []);
    }
    phase.get(task.file)!.push(task);
  });
  
  // Transform to PhaseGroups
  const phaseGroups: PhaseGroup[] = [];
  
  phaseMap.forEach((fileMap, phaseName) => {
    const allTasks: Task[] = [];
    const fileGroups = Array.from(fileMap.entries()).map(([path, fileTasks]) => {
      allTasks.push(...fileTasks);
      return {
        path: path.startsWith(scanDirectory) ? relative(process.cwd(), path) : path,
        phase: phaseName,
        milestone: path.split('/').pop() || path,
        tasks: fileTasks,
        progress: calculateProgress(fileTasks)
      };
    });
    
    phaseGroups.push({
      name: phaseName,
      milestone: \`\${phaseName} Tasks\`,
      description: \`Tasks in \${phaseName}\`,
      fileGroups,
      totalTasks: allTasks.length,
      completedTasks: allTasks.filter(t => t.completed).length
    });
  });
  
  // Sort phase groups alphabetically, but keep "General" at the end
  phaseGroups.sort((a, b) => {
    if (a.name === 'General') return 1;
    if (b.name === 'General') return -1;
    return a.name.localeCompare(b.name);
  });
  
  return phaseGroups;
}

function calculateProgress(tasks: Task[]): number {
  if (tasks.length === 0) return 0;
  const completed = tasks.filter(t => t.completed).length;
  return Math.round((completed / tasks.length) * 100);
}

function inferPriority(text: string): Task['priority'] {
  const lowercased = text.toLowerCase();
  if (lowercased.includes('urgent') || lowercased.includes('asap') || lowercased.includes('critical')) {
    return 'high';
  }
  if (lowercased.includes('low') || lowercased.includes('minor') || lowercased.includes('nice to have')) {
    return 'low';
  }
  return 'medium';
}
`;